import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { Box, Typography, Grid, Paper, Button } from '@mui/material';
import {
  EmojiEvents,
  Groups,
  Person,
  BarChart,
  Business,
  LocationOn,
  Gavel,
  ArrowForward,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { useHome, useLiveMatches } from '@/hooks/useApi';
import { useAdminMode } from '@/hooks/useManage';
import { MatchCard, TournamentCard, LoadingState, ErrorState, SportFilter } from '@/components';
import { ManagerPanel } from '@/components/ManagerPanel';
import type { SportType } from '@/types';

const actions: { path: string; label: string; icon: React.ReactNode }[] = [
  { path: '/tournaments', label: 'Турниры', icon: <EmojiEvents /> },
  { path: '/teams', label: 'Команды', icon: <Groups /> },
  { path: '/players', label: 'Игроки', icon: <Person /> },
  { path: '/venues', label: 'Площадки', icon: <LocationOn /> },
  { path: '/organizations', label: 'Организации', icon: <Business /> },
  { path: '/officials', label: 'Судьи', icon: <Gavel /> },
  { path: '/analytics', label: 'Аналитика', icon: <BarChart /> },
];

const DAY_MS = 24 * 60 * 60 * 1000;

const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

const shiftDateByDays = (date: Date, days: number): Date =>
  new Date(date.getTime() + days * DAY_MS);

const toIsoDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const parseIsoDateLocal = (value: string): Date => {
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return new Date(value);
  return new Date(year, month - 1, day);
};

const dateKeyFromDateTime = (value?: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return toIsoDate(date);
};

const formatDayLabel = (isoDate: string): string =>
  parseIsoDateLocal(isoDate).toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric' });

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { adminMode, manager } = useAdminMode();
  const [sportFilter, setSportFilter] = useState<SportType | null>(null);
  const dateParam = searchParams.get('date');
  const selectedDate = dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) ? dateParam : toIsoDate(startOfDay(new Date()));
  const [windowStartDate, setWindowStartDate] = useState<Date>(() => parseIsoDateLocal(selectedDate));
  const setSelectedDate = (date: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('date', date);
    setSearchParams(next, { replace: true });
  };
  const { data: liveMatches } = useLiveMatches();
  const weekFromDate = toIsoDate(windowStartDate);
  const weekToDate = toIsoDate(shiftDateByDays(windowStartDate, 6));

  // Week-range request: used for calendar strip (7 days + counters).
  const {
    data: weekData,
    isLoading: isWeekLoading,
    error: weekError,
    refetch: refetchWeek,
  } = useHome({
    sportType: sportFilter ?? undefined,
    fromDate: weekFromDate,
    toDate: weekToDate,
  });

  // Selected-day request: must fire on each day click.
  const {
    data: selectedDayData,
    isLoading: isSelectedDayLoading,
    error: selectedDayError,
    refetch: refetchSelectedDay,
  } = useHome({
    sportType: sportFilter ?? undefined,
    fromDate: selectedDate,
    toDate: selectedDate,
  });

  if (isWeekLoading || isSelectedDayLoading) return <LoadingState message="Загрузка..." />;
  if (weekError || selectedDayError) {
    return (
      <ErrorState
        message="Ошибка загрузки главной"
        onRetry={() => {
          refetchWeek();
          refetchSelectedDay();
        }}
      />
    );
  }

  const calendarDays =
    weekData?.calendarDays?.length
      ? weekData.calendarDays
      : Array.from({ length: 7 }, (_, dayIndex) => ({
        date: toIsoDate(shiftDateByDays(windowStartDate, dayIndex)),
        matchCount: 0,
      }));

  const calendarDaysWithFallbackDate = calendarDays.map((day, dayIndex) => ({
    ...day,
    date: day.date ?? toIsoDate(shiftDateByDays(windowStartDate, dayIndex)),
  }));

  const availableDateKeys = calendarDaysWithFallbackDate
    .map((day) => day.date)
    .filter((date): date is string => Boolean(date));
  const effectiveSelectedDate = availableDateKeys.includes(selectedDate)
    ? selectedDate
    : weekFromDate;

  const matchesUpcoming = selectedDayData?.matchesUpcoming ?? [];
  const popularTournaments = selectedDayData?.popularTournaments ?? weekData?.popularTournaments ?? [];
  const favorites = selectedDayData?.favorites ?? weekData?.favorites;
  const today = toIsoDate(startOfDay(new Date()));
  const liveForDay =
    effectiveSelectedDate === today
      ? (liveMatches?.data ?? [])
      : (liveMatches?.data ?? []).filter((match) => dateKeyFromDateTime(match.scheduledAt) === effectiveSelectedDate);
  const liveIds = new Set(liveForDay.map((match) => match.id));
  const dayMatches = [...liveForDay, ...matchesUpcoming.filter((match) => !liveIds.has(match.id))];

  return (
    <Box>
      <SportFilter attached selectedSport={sportFilter} onSelectSport={setSportFilter} />

      <Box sx={{ px: 2, pt: 1.5, pb: 2 }}>
      {adminMode && manager && <ManagerPanel manager={manager} />}

      {/* Календарная полоска (7 дней + стрелки без скролла) */}
      <Box sx={{ mb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '26px repeat(7, minmax(0, 1fr)) 26px',
            gap: 0.35,
            alignItems: 'stretch',
          }}
        >
          <Box
            component="button"
            type="button"
            aria-label="Предыдущие 7 дней"
            onClick={() => {
              const nextStart = shiftDateByDays(windowStartDate, -7);
              setWindowStartDate(nextStart);
              setSelectedDate(toIsoDate(nextStart));
            }}
            sx={{
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              borderRadius: 0.75,
              color: 'text.secondary',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              p: 0,
            }}
          >
            <ChevronLeft sx={{ fontSize: 18 }} />
          </Box>

          {calendarDaysWithFallbackDate.slice(0, 7).map((day, dayIndex) => {
            const dayDate = day.date ?? toIsoDate(shiftDateByDays(windowStartDate, dayIndex));
            const isSelected = dayDate === effectiveSelectedDate;
            return (
              <Box
                key={dayDate}
                component="button"
                type="button"
                onClick={() => setSelectedDate(dayDate)}
                sx={{
                  border: 1,
                  borderColor: isSelected ? 'primary.main' : 'divider',
                  bgcolor: isSelected ? 'primary.main' : 'background.paper',
                  color: isSelected ? 'primary.contrastText' : 'text.primary',
                  borderRadius: 0.75,
                  px: 0.15,
                  py: 0.5,
                  minWidth: 0,
                  cursor: 'pointer',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  lineHeight: 1.15,
                }}
              >
                <Typography variant="caption" sx={{ fontSize: '0.64rem', textTransform: 'capitalize', lineHeight: 1.05 }}>
                  {formatDayLabel(dayDate)}
                </Typography>
              </Box>
            );
          })}

          <Box
            component="button"
            type="button"
            aria-label="Следующие 7 дней"
            onClick={() => {
              const nextStart = shiftDateByDays(windowStartDate, 7);
              setWindowStartDate(nextStart);
              setSelectedDate(toIsoDate(nextStart));
            }}
            sx={{
              border: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              borderRadius: 0.75,
              color: 'text.secondary',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              p: 0,
            }}
          >
            <ChevronRight sx={{ fontSize: 18 }} />
          </Box>
        </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
        {dayMatches.length > 0 ? (
          dayMatches.map((match) => (
            <MatchCard
              key={match.id}
              match={match as import('@/types').Match}
              compact
              onClick={() => navigate(`/matches/${match.id}`)}
            />
          ))
        ) : (
          <Typography variant="body2" color="text.secondary">
            На выбранную дату матчей нет.
          </Typography>
        )}
      </Box>

      {/* Популярные турниры */}
      {popularTournaments.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography variant="h4">Турниры</Typography>
            <Button
              size="small"
              endIcon={<ArrowForward />}
              onClick={() => navigate('/tournaments')}
            >
              Все
            </Button>
          </Box>
          {popularTournaments.slice(0, 5).map((tournament) => (
            <TournamentCard
              key={tournament.id}
              tournament={tournament as import('@/types').Tournament}
              compact
              onClick={() => navigate(`/tournaments/${tournament.id}`)}
            />
          ))}
        </Box>
      )}

      {/* Избранное (при авторизации) */}
      {favorites && (favorites.tournaments?.length || favorites.matches?.length) ? (
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 1.5 }}>
            Избранное
          </Typography>
          {favorites.tournaments?.slice(0, 3).map((t) => (
            <TournamentCard
              key={t.id}
              tournament={t as import('@/types').Tournament}
              compact
              onClick={() => navigate(`/tournaments/${t.id}`)}
            />
          ))}
          {favorites.matches?.slice(0, 3).map((m) => (
            <MatchCard
              key={m.id}
              match={m as import('@/types').Match}
              compact
              onClick={() => navigate(`/matches/${m.id}`)}
            />
          ))}
        </Box>
      ) : null}

      <Box component="footer" sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Grid container spacing={1.5}>
          {actions.map(({ path, label, icon }) => (
            <Grid key={path} size={{ xs: 6, md: 3 }}>
              <Paper
                component="button"
                type="button"
                onClick={() => navigate(path)}
                sx={{
                  width: '100%',
                  minHeight: 56,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  cursor: 'pointer',
                  border: 'none',
                  bgcolor: 'background.paper',
                  color: 'text.primary',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box sx={{ color: 'primary.main', display: 'flex' }}>{icon}</Box>
                <Typography variant="body1" fontWeight={500}>
                  {label}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
      </Box>
    </Box>
  );
};
